/**
 * Money.
 *
 * ONE implementation, imported by the Worker and by the client — the same
 * reason `shared/time.ts` exists. A money function that disagrees with itself
 * across the wire is the worst kind of bug, because both halves look right in
 * isolation.
 *
 * ── The rule, and why it is absolute ─────────────────────────────────────────
 *
 * **Every monetary value in this application is an INTEGER number of cents.**
 * Not dollars, not a float, not a decimal string. SQLite's REAL is IEEE 754,
 * so `0.1 + 0.2 !== 0.3`; a landed cost is summed from twelve lines and then
 * compared against a ceiling that fires an alert and drives a Next Best
 * Action. That is precisely the arithmetic where a half-cent of drift becomes
 * a wrong decision about five figures of somebody's money.
 *
 * Rates are INTEGER basis points for the same reason: 10% is `1000`, and
 * `$3.25/mile` is `325` with unit `cents_per_mile`. A percentage written as
 * `0.10` smuggles a float straight back into the tax and contingency maths.
 *
 * **Rounding happens ONCE, at the final cents value of a line — never
 * mid-chain.** Every function here that can produce a fraction rounds at its
 * own boundary and returns an integer, so a caller composing them is never
 * holding a fractional cent to round twice.
 *
 * ── §3.7, §7 ─────────────────────────────────────────────────────────────────
 */

/** An integer number of cents. Never a float. Never dollars. */
export type Cents = number

/**
 * Integer basis points. 10% is `1000`, 100% is `10000`.
 *
 * One ten-thousandth is finer than any rate this application quotes, so the
 * integer form loses nothing and keeps the whole chain in integers.
 */
export type Bps = number

/**
 * Phase 1 accepts only USD. The type is a union of one rather than `string`
 * so that adding a second currency is a compile error at every site that has
 * to think about it, instead of a silent sum of two different monies.
 */
export const CURRENCIES = ['USD'] as const
export type CurrencyCode = (typeof CURRENCIES)[number]
export const USD: CurrencyCode = 'USD'

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value)
}

/** An amount with the currency attached, for anywhere the two can drift apart. */
export interface Money {
  readonly cents: Cents
  readonly currency: CurrencyCode
}

/**
 * A cost range.
 *
 * Three integers, never one column holding a string or a JSON pair — the
 * feed's main filter is `WHERE landed_expected_cents > ?` and it must hit an
 * index.
 *
 * The three readings are not interchangeable and the UI says so out loud:
 * `low` is "if everything goes right", `high` is "if everything goes wrong",
 * and `expected` is "plan against this" — the figure every threshold, alert
 * and Next Best Action rule compares against.
 */
export interface Triple {
  readonly low: Cents
  readonly expected: Cents
  readonly high: Cents
}

export const ZERO: Triple = Object.freeze({ low: 0, expected: 0, high: 0 })
export const ZERO_MONEY: Money = Object.freeze({ cents: 0, currency: USD })

/** A safe integer. The only shape a cents value is ever allowed to have. */
export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value)
}

export function money(cents: Cents, currency: CurrencyCode = USD): Money {
  return { cents, currency }
}

export function triple(low: Cents, expected: Cents, high: Cents): Triple {
  return { low, expected, high }
}

/** All three members are safe integers. */
export function isTriple(value: unknown): value is Triple {
  if (typeof value !== 'object' || value === null) return false
  const t = value as Record<string, unknown>
  return isCents(t.low) && isCents(t.expected) && isCents(t.high)
}

/**
 * `low <= expected <= high` — the CHECK constraint every cost row carries,
 * expressed once so the client can refuse the same input the database would.
 */
export function isOrdered(t: Triple): boolean {
  return t.low <= t.expected && t.expected <= t.high
}

/**
 * Force the ordering by widening, never by narrowing.
 *
 * A caller that has built a triple from three independent assumption bands can
 * legitimately produce `low > expected` when the library is mid-edit. Widening
 * keeps the honest reading ("this could be as cheap as X, as dear as Y");
 * clamping `low` up to `expected` would quietly hide that the library
 * disagrees with itself.
 */
export function order(t: Triple): Triple {
  return {
    low: Math.min(t.low, t.expected, t.high),
    expected: t.expected,
    high: Math.max(t.low, t.expected, t.high),
  }
}

/**
 * Apply a basis-point rate to a cents base. THE conversion point for a rate.
 *
 * `bps(690000, 1150)` — 11.50% use tax on $6,900 — is `79350`, exactly, with
 * no float in the chain until the single `Math.round` at the end.
 */
export function bps(baseCents: Cents, points: Bps): Cents {
  return Math.round((baseCents * points) / 10_000)
}

/**
 * A part of a whole, expressed in basis points. The inverse of `bps`.
 *
 * Used for variance ("this line came in 18.5% over estimate" → `1850`) and for
 * `actual_coverage_bps`. Returns 0 for a zero whole rather than NaN or
 * Infinity: "0% of nothing" is the only answer that can be rendered.
 */
export function toBps(part: Cents, whole: Cents): Bps {
  if (whole === 0) return 0
  return Math.round((part * 10_000) / whole)
}

/** Add two triples member-wise. */
export function addTriples(a: Triple, b: Triple): Triple {
  return { low: a.low + b.low, expected: a.expected + b.expected, high: a.high + b.high }
}

/**
 * Sum a list of triples member-wise.
 *
 * `low = Σ lows`, `expected = Σ expecteds`, `high = Σ highs`. Summing twelve
 * independent lows produces a floor that will never actually happen; that is
 * accepted and LABELLED in the UI rather than corrected. Root-sum-square is
 * statistically better and was rejected because it produces a narrower range
 * nobody can reproduce by adding up the visible column, and an unverifiable
 * number on a money screen is worse than a conservative one.
 */
export function sumTriples(triples: readonly Triple[]): Triple {
  let low = 0
  let expected = 0
  let high = 0
  for (const t of triples) {
    low += t.low
    expected += t.expected
    high += t.high
  }
  return { low, expected, high }
}

/**
 * Apply a per-band basis-point rate to a triple.
 *
 * The contingency line is the reason this exists: `contingency.bps` is seeded
 * 500 / 1000 / 1500, so the low band gets 5% and the high band 15%. One rate
 * across all three would invent a spread, which is the hardcoding §3.17
 * forbids.
 */
export function tripleBps(base: Triple, points: { low: Bps; expected: Bps; high: Bps }): Triple {
  return {
    low: bps(base.low, points.low),
    expected: bps(base.expected, points.expected),
    high: bps(base.high, points.high),
  }
}

/** Result of a sum that could have been asked to add two different currencies. */
export type SumResult =
  | { ok: true; total: Money }
  | { ok: false; code: 'mixed_currency'; currencies: string[] }

/**
 * Sum money, REFUSING a mixed-currency list rather than silently adding it.
 *
 * A typed refusal, not a throw: this runs in a render path on the client as
 * well as in a handler, and an exception there blanks a screen.
 */
export function sumMoney(values: readonly Money[]): SumResult {
  if (values.length === 0) return { ok: true, total: ZERO_MONEY }
  const currencies = [...new Set(values.map((v) => v.currency))]
  if (currencies.length > 1) return { ok: false, code: 'mixed_currency', currencies }
  const currency = currencies[0] ?? USD
  let total = 0
  for (const v of values) total += v.cents
  return { ok: true, total: { cents: total, currency } }
}

export interface DisplayOptions {
  /** BCP-47 tag. `es-PR` renders the same digits with Puerto Rican grouping. */
  locale?: string
  currency?: CurrencyCode
  /**
   * Show the two decimal places.
   *
   * Default FALSE. A feed card showing `$10,797` reads at a glance; the same
   * card showing `$10,796.75` claims a precision the estimate does not have.
   * The cost breakdown, where the column has to add up in front of a person,
   * passes `true`.
   */
  showCents?: boolean
}

/**
 * Cents to a string a person reads. The ONLY place cents become dollars.
 *
 * Division by 100 is the one unavoidable float in the file and it happens at
 * the very last step, on a value that is already final — never on something
 * another calculation will consume.
 */
export function centsToDisplay(cents: Cents, options: DisplayOptions = {}): string {
  const { locale = 'en-US', currency = USD, showCents = false } = options
  const digits = showCents ? 2 : 0
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100)
}

/** The en dash, not a hyphen: a range, not a subtraction. */
const RANGE_DASH = ' – '

/**
 * `"$9,048 – $12,785"` — the low and the high, which is what a card shows.
 */
export function formatRange(t: Triple, options: DisplayOptions = {}): string {
  return `${centsToDisplay(t.low, options)}${RANGE_DASH}${centsToDisplay(t.high, options)}`
}

/**
 * `"$9,048 – $10,797 – $12,785"` — all three, for the breakdown screen, where
 * the expected figure is the one set in the largest, boldest type.
 */
export function formatTriple(t: Triple, options: DisplayOptions = {}): string {
  return [t.low, t.expected, t.high].map((c) => centsToDisplay(c, options)).join(RANGE_DASH)
}

/**
 * Parse what a person typed into integer cents. Total: `null`, never a throw.
 *
 * Accepts `$6,900`, `6900`, `6,900.00`, `-125.50`, `  $ 6900.5 `.
 *
 * The dollars and the fraction are parsed as SEPARATE INTEGERS and combined
 * with integer arithmetic. `parseFloat('6900.10') * 100` is 690009.99999…,
 * which rounds correctly today and will not on some other value — that is the
 * whole class of bug this file exists to make impossible, and it would be
 * absurd to reintroduce it at the front door.
 *
 * More than two decimal places is a typo, not an amount; it is rounded to the
 * cent rather than refused, because losing what somebody typed at a gate is
 * worse than accepting a keystroke they will see rendered back to them.
 */
export function parseDollarsToCents(input: string | number | null | undefined): Cents | null {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null
    return Math.round(input * 100)
  }
  if (typeof input !== 'string') return null

  const cleaned = input.trim().replace(/[$,\s]/g, '')
  if (cleaned === '') return null

  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(cleaned)
  if (!match) return null

  const [, sign = '', whole = '', fraction] = match
  // "." and "" and "-" are not amounts. At least one digit is required.
  if (whole === '' && (fraction === undefined || fraction === '')) return null

  const dollars = whole === '' ? 0 : Number(whole)
  if (!Number.isSafeInteger(dollars)) return null

  let cents = 0
  if (fraction !== undefined && fraction !== '') {
    // Pad or round to exactly two places, as integers.
    const twoPlaces = fraction.slice(0, 2).padEnd(2, '0')
    cents = Number(twoPlaces)
    const third = fraction.charCodeAt(2)
    // charCodeAt is NaN past the end, and NaN >= 53 is false — no third digit
    // means no rounding, without a second length check.
    if (third >= 0x35) cents += 1
  }

  const total = dollars * 100 + cents
  if (!Number.isSafeInteger(total)) return null
  return sign === '-' ? -total : total
}
